import { useEffect, useRef, useState } from 'react';
import { markJustJoined } from './getapp.jsx';
import { joinStart, joinVerify, setPassword, saveMe, saveToken, announceSignedIn, call, getToken } from './api.js';
import { GOOGLE_KEY } from './google.jsx';
import { go } from './nav.js';
import { returnParam, withReturn } from './gate.jsx';
import { problem } from './password.js';
import { PwChecklist } from './pwcheck.jsx';
import { Eye } from './Eye.jsx';
/* ⚠️ ONLY TO READ THE COUNTRY CODE ELEAT ALREADY RESOLVED. Joining does not
   set, move or depend on a discovery centre — see `joinCountry` below. */
import { readCentre, whereCity } from './place.js';
import { visitLanguage, adoptAccountLanguage } from './voice.js';
import { PhoneRow, joinPhone, deviceDial, maskPhone, useChannels } from './phone.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * JOINING ELEAT — THE DOOR THAT DID NOT EXIST
 *
 * ⚠️ UNTIL TODAY NOBODY COULD JOIN. SignIn.jsx carried a button reading "New
 * to Eleat? Create an account" whose entire behaviour was to print *"Not built
 * yet — joining is next after the reset."* Everything built in React over the
 * last three days — Home, Explore, the venue, Saved, Profile, Zaryna — was for
 * people who already had an account, and the only way to get one was a script
 * run by hand against the database.
 *
 * ── EVERY WORD IS QUOTED FROM `04_Email_SignUp.html` ────────────────────────
 *
 * "STEP 1 OF 2" · "Create your account" · "Your name, date of birth and number
 * — about a minute." · "Your name" · "Date of birth" · "Asked once, and it
 * cannot be changed afterwards." · "Email address" · "Mobile number" · "Eleat
 * sends your reminders here. Never passed on." · "Password" · "A few words you
 * will remember beats a short one with symbols in it." · "Confirm password" ·
 * "Send my code by email" · "* Required" · "Arrives in seconds · check your
 * spam folder if it doesn't" · "STEP 2 OF 2" · "Enter the 6-digit code" · "We
 * sent it to" · "Didn't get it?" · "Send it again" · "Verify".
 *
 * RULE 4: DO NOT INVENT ELEAT. If a word here is wrong it is wrong on that
 * screen too, and both change together.
 *
 * ── THE ORDER IS AL'S, AND IT IS NOT THE OBVIOUS ONE ────────────────────────
 *
 * Al, 16 September 2026: *"Why do you have a skip in the password and
 * confirmation? No one does that ever… phone number, password, confirmation of
 * password, sign in OTP. That's the way it works."*
 *
 * So: the details and the password are chosen FIRST, and the code comes last.
 * The code proves the address, and always did. There is no skip.
 *
 * ── WHICH MEANS THE PASSWORD IS HELD IN MEMORY BETWEEN THE TWO STEPS ────────
 *
 * ⚠️ AND THAT IS THE ONE UNCOMFORTABLE THING IN THIS FILE, SO IT IS WRITTEN
 * DOWN RATHER THAN LEFT TO BE DISCOVERED. `/auth/eleater/start` takes no
 * password — there is no account to set one on yet. `/auth/password/set` is
 * `requireAuth` and needs the token that only `/auth/eleater/verify` returns.
 * So the password sits in React state across step 2.
 *
 * It is never written to localStorage, never to sessionStorage, never put in a
 * URL, and it is cleared the moment it has been set. That is the whole of the
 * mitigation and it is honest about what it is: a value in memory on the
 * Eleater's own device, for about thirty seconds.
 *
 * ── AND THE NUMBER IS SAVED AFTERWARDS, ON PURPOSE ──────────────────────────
 *
 * ⚠️ SEE api.js `joinVerify`. Sending the phone to /verify alongside the email
 * would verify the PHONE against an emailed code and set `phone_verified` on a
 * number nobody proved they hold — defect B1 in a new hat. It goes through
 * PUT /settings/me instead, which sets `phone_verified = false`.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ THIS SENT `market: 'hong-kong'` AND THE SERVER ACCEPTS NO SUCH VALUE.
 *
 * `auth.routes.js`: `z.enum(['CA', 'US', 'HK', 'CN', 'OTHER'])`. So every join
 * request Eleat has ever made was rejected by validation before it reached one
 * line of joining code. **Nobody could join.**
 *
 * ⚠️ AND IT IS A COMPLETELY DIFFERENT THING FROM THE DISCOVERY MARKET, which
 * is what made one word look like it belonged in both. Al, 25 September 2026:
 * *"Do NOT merge account/billing country with discovery market. They are
 * separate concepts. A user registered under HK can later physically be in
 * Calgary and search Calgary."*
 *
 *   `users.market`     COUNTRY. Billing and tax. Set once, at joining.
 *   discovery market   A CITY — 'hong-kong', 'calgary'. Changes as they move.
 *
 * ⚠️ IT IS DERIVED, NEVER GUESSED. `/discovery/where` already puts a real ISO
 * country code on the stored centre, anywhere on earth — so somebody joining
 * in Hong Kong sends HK and somebody joining in Calgary sends CA, without
 * either of them being asked a question the locked screen does not have.
 *
 * ⚠️ AND WHEN ELEAT DOES NOT KNOW, IT SENDS NOTHING. The field is
 * `market.optional()` on both join routes, so an absent country is a shape the
 * server already handles. Defaulting to HK would write a false billing country
 * onto a real account to avoid leaving a column null — RULE 4, and the harder
 * thing to unpick later.
 * ══════════════════════════════════════════════════════════════════════════ */
const BILLING_COUNTRIES = ['CA', 'US', 'HK', 'CN'];

function joinCountry() {
  const c = readCentre();
  const iso = c && c.country_code ? String(c.country_code).toUpperCase() : null;
  if (!iso) return undefined;
  return BILLING_COUNTRIES.includes(iso) ? iso : 'OTHER';
}

/* ⚠️ THE MONTHS ARE NAMED, NOT NUMBERED, AND THAT IS A CORRECTNESS CHOICE.
   A three-box date where the middle box is a number is read one way in Hong
   Kong and another in Calgary, and 03/04 is two different days. The locked
   screen uses a Month dropdown; this is that dropdown's contents. */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/* ⚠️ THE YEARS RUN BACK FROM THIS YEAR AND THE LIST NAMES NO AGE.
   Al, 10 September 2026: *"if we tell them about age gate they can lie about
   their age."* A year list that stops at "this year minus 18" tells a
   seventeen-year-old both the threshold and what to pick. It runs to this year
   and the gate is decided by the date, in the database, where it cannot be
   read off a dropdown. */
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 110 }, (_, i) => THIS_YEAR - i);

/** yyyy-mm-dd, or null. The API's own comment: assembling it is the client's
 *  job, "because a date split across three fields is a widget detail". */
function isoDate(d, m, y) {
  if (!d || !m || !y) return null;
  const mi = MONTHS.indexOf(m);
  if (mi < 0) return null;
  const iso = `${y}-${String(mi + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  /* ⚠️ 31 FEBRUARY IS PICKABLE IN THREE DROPDOWNS AND IS NOT A DATE. Postgres
     would refuse it with a 500 that says nothing useful; this catches it here
     and the screen says so in words. */
  const back = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(back.getTime())) return null;
  if (back.getUTCDate() !== Number(d) || back.getUTCMonth() !== mi) return null;
  return iso;
}

export default function Join() {
  const [step, setStep] = useState(1);

  /* step 1 */
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dobD, setDobD] = useState('');
  const [dobM, setDobM] = useState('');
  const [dobY, setDobY] = useState('');
  const [email, setEmail] = useState('');
  const [num, setNum] = useState('');
  const [dial, setDial] = useState(deviceDial);
  /* ⚠️ THE WHATSAPP DOOR — `?join=1&by=whatsapp`, `whatsapp_onboarding_new.html`
     (8 Oct 2026). The number IS the account here: no email box, the code goes
     to WhatsApp, and the number is saved verified by the code itself. Only when
     the server says WhatsApp can send — otherwise this is the email door. */
  const { whatsapp: waOn } = useChannels();
  const byWa = waOn && new URLSearchParams(window.location.search).get('by') === 'whatsapp';
  const phone = joinPhone(dial, num);
  /* ⚠️ THE GOOGLE DOOR — `?join=1&by=google`, 03b_Google_Continue.html (28 Oct
     2026). Google has already said who they are and Eleat has signed them in;
     this is "Three more things and you are in": name, date of birth, mobile. */
  const byGoogle = !!getToken() && new URLSearchParams(window.location.search).get('by') === 'google';
  const [gmail, setGmail] = useState('');
  useEffect(() => {
    if (!byGoogle) return;
    try {
      const g = JSON.parse(sessionStorage.getItem(GOOGLE_KEY) || '{}');
      if (g.email) setGmail(g.email);
      if (g.first_name) setFirst(g.first_name);
      if (g.last_name) setLast(g.last_name);
    } catch { /* fine */ }
  }, [byGoogle]);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  /* step 2 */
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [resent, setResent] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const codeRef = useRef(null);

  useEffect(() => { if (step === 2 && codeRef.current) codeRef.current.focus(); }, [step]);

  /* ⛔⛔ THERE IS NO BUTTON ON THE JOINED SCREEN — AL, 30 SEPTEMBER 2026.
     *"the find somewhere to eat button after joining. That was made up. I
     never had that. So please remove it… Not ever to be done."*
     `07_Welcome.html` has no control at all: three dots, then it fades on by
     itself — back to the exact spot when a gated action sent them here
     (3.6s), to Home on a cold join (4.2s). Those are its own numbers. */
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (step !== 3) return undefined;
    const dest = returnParam();
    const t1 = setTimeout(() => setLeaving(true), dest === '?' ? 4200 : 3600);
    /* ⚠️ IN PLACE, NOT A RELOAD — Al-Noor, 9 Oct 2026 (signing in "took a
       while… to put me onto the home page"). The joined screen keeps its own
       timing; what follows it no longer fetches the page and code again. */
    const t2 = setTimeout(() => { announceSignedIn(); go(dest, { replace: true }); }, (dest === '?' ? 4200 : 3600) + 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step]);

  const dob = isoDate(dobD, dobM, dobY);
  const pwProblem = problem(pw, pw2);

  /* ⚠️ EVERY FIELD MARKED `*` ON THE LOCKED SCREEN IS REQUIRED HERE, and the
     button is disabled until they are all there — which is what "* Required"
     at the foot of that screen promises. A button that is enabled and then
     refuses is a button that lied. */
  /* ⚠️ THE BUTTON IS NEVER GREYED OUT — Al-Noor, 10 Oct 2026, testers in
     Calgary: "It won't let me register. The send my code button is greyed
     out." A disabled button cannot say what is wrong, and the missing field
     may be off the screen (best practice: Carta, Case Western accessibility,
     web.dev — leave it pressable, then say what is missing and go there).
     Pressing it with something missing marks each field and scrolls to the
     first one. */
  const [tried, setTried] = useState(false);
  const [why, setWhy] = useState(false);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const missing = {
    first: first.trim() === '' ? 'Add your first name.' : null,
    dob: dob === null ? 'Choose your date of birth.' : null,
    email: byWa ? null : (email.trim() === '' ? 'Add your email address.'
      : (!emailOk ? 'Check your email address.' : null)),
    phone: phone === '' ? 'Add your mobile number.' : null,
    pw: pwProblem,
  };
  if (byGoogle) { missing.email = null; missing.pw = null; }
  const ready = !Object.values(missing).some(Boolean) && !busy;
  const who = byWa ? { phone } : { email };

  async function sendCode(e) {
    if (e) e.preventDefault();
    if (busy) return;
    if (!ready) {
      setTried(true);
      const first = ['first', 'dob', 'email', 'phone', 'pw'].find((k) => missing[k]);
      const el = first && document.getElementById('join-' + first);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); if (el.focus) el.focus({ preventScroll: true }); }
      return;
    }
    setBusy(true); setError(null); setResent('');
    try {
      const d = await joinStart({ ...who, market: joinCountry() });
      setSentTo(byWa ? maskPhone(d?.sent_to || phone) : (d?.sent_to || email.trim().toLowerCase()));
      /* ⚠️ `dev_code` COMES BACK ONLY WHEN OTP_ECHO_FOR_DEVELOPMENT IS ON, and
         its own comment says that must never be on in a deployed environment.
         It is filled in here so the step can be walked on a developer's
         machine — and it is NEVER stored, logged or shown as though Eleat had
         sent it. If it is absent, nothing here pretends. */
      if (d?.dev_code) setCode(String(d.dev_code));
      setStep(2);
    } catch (err) {
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  async function resend() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await joinStart({ ...who, market: joinCountry() });
      setResent('Sent again.');
    } catch (err) {
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  async function verify(e) {
    e.preventDefault();
    if (busy) return;
    /* never greyed out (Al-Noor, 10 Oct 2026): pressed too early it says what is missing */
    if (code.trim().length < 4) { setError({ text: 'Enter the code we sent you.' }); return; }
    setBusy(true); setError(null);
    try {
      /* 1 — the code becomes an account and a token */
      const d = await joinVerify({
        ...who, code, new_password: pw, first_name: first.trim(),
        last_name: last.trim() || undefined, date_of_birth: dob, market: joinCountry(),
        city: whereCity() || undefined,
      });
      if (!d?.token) throw new Error('Eleat did not send a sign-in back. Try again.');

      /* ⚠️ THE TOKEN IS SAVED BEFORE THE NEXT TWO CALLS, because both are
         `requireAuth` and read it out of storage. `stay` is true: somebody who
         has just joined on their own phone is not asked to sign in again on
         the next screen. */
      saveToken(d.token, true);

      /* 2 — the password they chose at step 1. The server saves it with the
         code now (7 Oct 2026: re-joining an existing account could not set it
         here); this call is only for a server that has not been updated. */
      if (!d.password_set) {
        /* setting a password ends the other sessions and hands this device a
           NEW token — keep it, or the next call is "Signed out" (audit, 7 Oct) */
        const s = await setPassword({ new_password: pw, confirm_password: pw2 });
        if (s?.token) saveToken(s.token, true);
      }

      /* ⚠️ CLEARED THE INSTANT IT IS NO LONGER NEEDED. */
      setPw(''); setPw2('');

      /* 3 — the number, which /verify deliberately never saw.
         ⚠️ THIS ONE IS ALLOWED TO FAIL WITHOUT LOSING THE ACCOUNT. They are
         joined, signed in and have a password; a number that did not save is a
         thing they can fix in Settings, and throwing them back to step 1 over
         it would destroy an account that already exists and cannot be made
         again with the same address. */
      try {
        /* on the WhatsApp door the number is already on the account, verified */
        if (!byWa) await saveMe({ phone });
      } catch { /* they are in — Settings has the field */ }

      /* the language they were reading Eleat in becomes their account's —
         otherwise joining in 繁體 would flip them to English (Al, 6 Oct 2026:
         signed in, the profile's language is the language) */
      const seen = visitLanguage();
      if (seen && !/^en/i.test(seen)) {
        try { await saveMe({ preferred_language: seen }); adoptAccountLanguage(seen); } catch { /* Settings has it */ }
      }

      setStep(3);
      markJustJoined();   /* once in the stores: one "Eleat works best in the app" screen */
    } catch (err) {
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  /* 03b — the three things Google does not give us, then the joined screen */
  async function finishGoogle(e) {
    e.preventDefault();
    if (busy) return;
    if (!ready) {
      setTried(true);
      const f = ['first', 'dob', 'phone'].find((k) => missing[k]);
      const el = f && document.getElementById('join-' + f);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); if (el.focus) el.focus({ preventScroll: true }); }
      return;
    }
    setBusy(true); setError(null);
    try {
      await call('/auth/google/finish', { method: 'POST', auth: true, body: {
        first_name: first.trim(), last_name: last.trim() || undefined, date_of_birth: dob, phone } });
      try { sessionStorage.removeItem(GOOGLE_KEY); } catch { /* fine */ }
      const seen = visitLanguage();
      if (seen && !/^en/i.test(seen)) {
        try { await saveMe({ preferred_language: seen }); adoptAccountLanguage(seen); } catch { /* Settings has it */ }
      }
      setStep(3);
      markJustJoined();
    } catch (err) {
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  /* ── STEP 3 · 07_Welcome.html ─────────────────────────────────────────── */
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️ THIS NOTE SITS ABOVE THE `return`, NOT INSIDE IT. A comment written as
     the first thing inside a JSX return or a `.map(` is the exact shape that
     stopped the whole app building on 26 September and hid a day's work from
     Al's phone. Prose about a call goes above the call.
     ══════════════════════════════════════════════════════════════════════ */
  /* ══════════════════════════════════════════════════════════════════════
         ⚠️⚠️⚠️ THIS SCREEN WORE ANOTHER SCREEN'S CLASS — 29 SEPTEMBER 2026.

         Al: *"welcome to Eleat. You are now an Eleater, the elevated foodie,
         and then find somewhere to eat. That doesn't sit properly. And look
         at the spacing, look at the justification."*

         **`className="screen welcome"` — and `.screen.welcome` in the CSS is
         `02_Welcome.html`, a completely different screen.** Those rules are
         written for `> header`, `.main`, `.copy` and `> footer`, none of which
         are here, and the first thing they do is `padding:0`, which cancels
         `.screen`'s own 24px/20px. So this screen had NO side padding, no
         centring and no type of its own: the heading sat hard against the
         left edge of the phone. Nothing threw — the rules all applied, they
         just belonged to somebody else.

         ⚠️ IT IS `.joined` NOW, AND THE RULES COME OUT OF `07_Welcome.html`.
         That file is centred both ways (`align-items:center;
         justify-content:center; text-align:center`), and that is the whole of
         what Al means by the justification being wrong.

         ⚠️ AND THE NAME IS THE LOCKED SCREEN'S OWN `h1 .nm` — a block line
         under "Welcome to Eleat", in persimmon, larger than the line above
         it. **NO COMMA**, which is Al's own instruction of 10 September:
         *"you've got a comma and then AP. You don't need that comma there."*
         Two lines already do a comma's job. With no name the heading simply
         ends after "Eleat" and still reads as a finished sentence — the
         locked file's own rule, and it refuses a stand-in outright: *"A
         placeholder standing in for a person's name is worse than no name."*

         ⚠️ ONE LOCKUP, NOT A MARK AND A WORDMARK STACKED BY HAND. That is a
         second version of an image Eleat already has, and `eleat_lockup.png`
         is what `07_Welcome.html` draws.

         ⚠️ THE BUTTON STAYS. The locked screen has three blinking dots and
         fades into Home by itself, with no control at all — turning this into
         an auto-advance would be removing something Al can tap, which is a
         product decision made during a spacing fix (RULE 3). The dots are NOT
         copied across for the same reason in reverse: they promise a screen
         that moves on its own, and this one waits.
         ══════════════════════════════════════════════════════════════════ */
  if (step === 3) {
    return (
      <div className={leaving ? 'screen joined leaving' : 'screen joined'}>
        <img className="lockup" src="/eleat_lockup.png" alt="Eleat" />
        <main>
          {/* verbatim, 07_Welcome.html */}
          <h1>Welcome to Eleat{first.trim() && <span className="nm">{first.trim()}</span>}</h1>
          <p className="tag">You are now an <b>Eleater</b> … the elevated foodie!</p>
        </main>
        {/* `07_Welcome.html`'s own three dots — the screen moves on by itself. */}
        <div className="dots" aria-hidden="true">
          <span className="dot" /><span className="dot" /><span className="dot" />
        </div>
      </div>
    );
  }

  /* ── STEP 2 · the code ────────────────────────────────────────────────── */
  if (step === 2) {
    return (
      <form className="screen" onSubmit={verify} noValidate>
        <div className="top">
          <button className="back" type="button" aria-label="Back"
            onClick={() => { setStep(1); setError(null); }}>‹</button>
          <div className="steps">STEP 2 OF 2</div>
        </div>
        <div className="logo">
          <img className="lmark" src="/eleat_mark.png" alt="" />
          <img className="lword" src="/eleat_word.png" alt="Eleat" />
        </div>
        <main>
          <h1>Enter the 6-digit code</h1>
          {/* ⚠️ THE ADDRESS IS SHOWN AS THE SERVER REPORTED IT, not as it was
              typed. `sent_to` is where the code actually went, and if the two
              ever differ the Eleater needs to see the one that is true. */}
          {byWa
            ? <p className="sub">We sent it on WhatsApp to <b>{sentTo}</b></p>
            : <p className="sub">We sent it to <b>{sentTo}</b></p>}

          <input
            ref={codeRef}
            className="tin otp1"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            aria-label="Enter the 6-digit code"
            value={code}
            /* ⚠️ DIGITS ONLY, AND SPACES STRIPPED. A code pasted out of an
               email arrives as " 481 902 " often enough to matter, and a
               server that answers "that code is wrong" to a correct code with
               a space in it is a support call nobody can diagnose. */
            onChange={(ev) => setCode(ev.target.value.replace(/\D/g, ''))}
          />

          <div className="resend">
            <span className="mut">Didn’t get it?</span>{' '}
            <button className="link" type="button" onClick={resend}>
              {byWa ? 'Resend on WhatsApp' : 'Send it again'}
            </button>
          </div>
          {resent && <div className="mut" aria-live="polite">{resent}</div>}

          <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
            {error?.text || ''}
          </div>
        </main>
        <footer>
          <button className="cta" type="submit">
            Verify
          </button>
          {byWa
            ? <div className="deliv">Standard WhatsApp message rates may apply.</div>
            : <div className="deliv">Arrives in seconds · check your spam folder if it doesn’t</div>}
        </footer>
      </form>
    );
  }

  /* ── 03b · "Google confirmed you" ─────────────────────────────────────── */
  if (byGoogle) {
    return (
      <form className="screen" onSubmit={finishGoogle} noValidate>
        <div className="top">
          <a className="back" href={withReturn('?welcome=1', returnParam())} aria-label="Back">‹</a>
          <div className="steps">STEP 2 OF 2</div>
        </div>
        <div className="logo">
          <img className="lmark" src="/eleat_mark.png" alt="" />
          <img className="lword" src="/eleat_word.png" alt="Eleat" />
        </div>
        <main>
          <h1>Google confirmed you</h1>
          <p className="sub">Three more things and you are in.</p>
          {gmail && <div className="gwho"><span className="gwho-k">GOOGLE</span><span>{gmail}</span></div>}

          <div className="label">Your name <i className="req" aria-hidden="true">*</i></div>
          <div className="namerow">
            <input className="tin" type="text" autoComplete="given-name" id="join-first"
              placeholder="First name" aria-label="First name"
              value={first} onChange={(e) => setFirst(e.target.value)} />
            <input className="tin" type="text" autoComplete="family-name"
              placeholder="Last name (optional)" aria-label="Last name (optional)"
              value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
          {tried && missing.first && <div className="fieldmiss" aria-live="polite">{missing.first}</div>}

          <div className="label">Date of birth <i className="req" aria-hidden="true">*</i></div>
          <div className="dobrow">
            <select className="dsel" aria-label="Day" value={dobD} id="join-dob"
              onChange={(e) => setDobD(e.target.value)}>
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) =>
                <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="dsel" aria-label="Month" value={dobM}
              onChange={(e) => setDobM(e.target.value)}>
              <option value="">Month</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className="dsel yr" aria-label="Year" value={dobY}
              onChange={(e) => setDobY(e.target.value)}>
              <option value="">Year</option>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="dobnote">Asked once, and it cannot be changed afterwards.</div>
          {dobD && dobM && dobY && !dob && (
            <div className="pwhint" aria-live="polite">That day is not in that month.</div>
          )}
          {tried && missing.dob && !(dobD && dobM && dobY) && <div className="fieldmiss" aria-live="polite">{missing.dob}</div>}

          <div className="label" style={{ marginTop: 16 }}>
            Mobile number <i className="req" aria-hidden="true">*</i>
          </div>
          <div id="join-phone" tabIndex={-1}>
            <PhoneRow dial={dial} number={num} onDial={setDial} onNumber={setNum} />
          </div>
          {tried && missing.phone && <div className="fieldmiss" aria-live="polite">{missing.phone}</div>}
          <div className="dobnote">Eleat sends your reminders here. Never passed on.</div>

          <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
            {error?.text || ''}
          </div>
        </main>
        <footer>
          <button className="cta" type="submit">Continue into Eleat</button>
          <div className="legend"><i className="req" aria-hidden="true">*</i> Required</div>
          <div className="fine">By continuing you agree to Eleat’s Terms &amp; Privacy.</div>
        </footer>
      </form>
    );
  }

  /* ── STEP 1 · the details and the password ────────────────────────────── */
  return (
    <form className="screen" onSubmit={sendCode} noValidate>
      <div className="top">
        {/* ⚠️ `04_Email_SignUp.html` #b1: *"BACK FROM STEP 1 LEAVES SIGN-UP"* — to
            the Welcome door, carrying the way back, not to the sign-in form. */}
        <a className="back" href={withReturn('?welcome=1', returnParam())} aria-label="Back">‹</a>
        <div className="steps">STEP 1 OF 2</div>
      </div>
      <div className="logo">
        <img className="lmark" src="/eleat_mark.png" alt="" />
        <img className="lword" src="/eleat_word.png" alt="Eleat" />
      </div>

      <main>
        <h1>Join Eleat</h1>
        <p className="sub">Your name, date of birth and number — about a minute.</p>

        <div className="label">Your name <i className="req" aria-hidden="true">*</i></div>
        <div className="namerow">
          <input className="tin" type="text" autoComplete="given-name" id="join-first"
            placeholder="First name" aria-label="First name"
            value={first} onChange={(e) => setFirst(e.target.value)} />
          <input className="tin" type="text" autoComplete="family-name"
            placeholder="Last name (optional)" aria-label="Last name (optional)"
            value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        {tried && missing.first && <div className="fieldmiss" aria-live="polite">{missing.first}</div>}

        <div className="label">Date of birth <i className="req" aria-hidden="true">*</i></div>
        <div className="dobrow">
          <select className="dsel" aria-label="Day" value={dobD} id="join-dob"
            onChange={(e) => setDobD(e.target.value)}>
            <option value="">Day</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) =>
              <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="dsel" aria-label="Month" value={dobM}
            onChange={(e) => setDobM(e.target.value)}>
            <option value="">Month</option>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="dsel yr" aria-label="Year" value={dobY}
            onChange={(e) => setDobY(e.target.value)}>
            <option value="">Year</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {/* ⚠️ A NEUTRAL AGE GATE — IT DOES NOT NAME THE AGE. The locked
            screen's own note, and its reason: "if we tell them about age gate
            they can lie about their age." No number, no consequence, no
            default value. */}
        <div className="dobnote">Asked once, and it cannot be changed afterwards.</div>
        {/* "Why we ask" — Al-Noor yes, 21 Oct 2026, after Google's own sign-up
            ("Why we ask for your date of birth and gender"). Eleat's words; it
            names no age, as the note above says. */}
        <button type="button" className="whyask" onClick={() => setWhy(true)}>Why we ask</button>
        {why && (
          <div className="egate" role="dialog" aria-modal="true" aria-labelledby="whyaskH">
            <div className="egate-veil" onClick={() => setWhy(false)} />
            <div className="egate-card whyask-card">
              <div className="egate-grip" />
              <h3 className="egate-h" id="whyaskH">Why we ask</h3>
              <div className="whyask-row"><b>Date of birth</b>
                <p>Required. It helps Eleat suit what you see to you. Restaurants and other Eleaters never see it, and it cannot be changed afterwards.</p></div>
              <div className="whyask-row"><b>Email address</b>
                <p>For your sign-in codes, and to help you get back in if you forget your password.</p></div>
              <div className="whyask-row"><b>Mobile number</b>
                <p>Eleat sends your reminders here. Never passed on.</p></div>
              <div className="whyask-row"><b>Gender</b>
                <p>Optional, and only asked in Settings. It also helps Eleat suit what you see to you.</p></div>
              <button className="egate-go" type="button" onClick={() => setWhy(false)}>Got it</button>
            </div>
          </div>
        )}
        {/* said only when the three boxes make an impossible day */}
        {dobD && dobM && dobY && !dob && (
          <div className="pwhint" aria-live="polite">That day is not in that month.</div>
        )}
        {tried && missing.dob && !(dobD && dobM && dobY) && <div className="fieldmiss" aria-live="polite">{missing.dob}</div>}

        {!byWa && (<>
          <div className="label">Email address <i className="req" aria-hidden="true">*</i></div>
          <input className="tin" type="email" inputMode="email" autoComplete="email" id="join-email"
            autoCapitalize="off" spellCheck={false} placeholder="you@example.com"
            aria-label="Email address"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          {tried && missing.email && <div className="fieldmiss" aria-live="polite">{missing.email}</div>}
        </>)}

        {/* ⚠️ THE NUMBER IS A JOINING FIELD — asked once, when the account is
            made, and never on the way back in on the email door.
            ⚠️ THE LOCKED SCREEN'S COUNTRY-CODE PICKER IS HERE NOW (8 Oct 2026,
            `whatsapp_onboarding_new.html` .phonerow): the dialling code, then
            the number, saved as "+" and the digits — a number typed without
            its code was saved as typed and could never be matched or reached. */}
        <div className="label" style={{ marginTop: byWa ? 0 : 16 }}>
          Mobile number <i className="req" aria-hidden="true">*</i>
        </div>
        <div id="join-phone" tabIndex={-1}>
          <PhoneRow dial={dial} number={num} onDial={setDial} onNumber={setNum} />
        </div>
        {tried && missing.phone && <div className="fieldmiss" aria-live="polite">{missing.phone}</div>}
        {!byWa && <div className="dobnote">Eleat sends your reminders here. Never passed on.</div>}

        <div className="label" style={{ marginTop: 16 }}>
          Password <i className="req" aria-hidden="true">*</i>
        </div>
        <div className="pwwrap">
          <input className="tin" type={showPw ? 'text' : 'password'}
            /* new-password, so a manager OFFERS one instead of filling the
               last one it saw */
            autoComplete="new-password" placeholder="Choose a password" id="join-pw"
            aria-label="Password" aria-describedby="join-pwcheck"
            value={pw} onChange={(e) => setPw(e.target.value)} />
          <Eye on={showPw} onToggle={() => setShowPw((v) => !v)} />
        </div>
        {/* the whole rule, always, each line ticked as it is met — Al-Noor,
            9 and 10 Oct 2026 */}
        <PwChecklist pw={pw} id="join-pwcheck" />

        <div className="label" style={{ marginTop: 16 }}>
          Confirm password <i className="req" aria-hidden="true">*</i>
        </div>
        <div className="pwwrap">
          <input className="tin" type={showPw2 ? 'text' : 'password'}
            autoComplete="new-password" placeholder="Type it again"
            aria-label="Confirm password"
            value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <Eye on={showPw2} onToggle={() => setShowPw2((v) => !v)} />
        </div>
        {/* ⚠️ THE MISMATCH IS SAID THE MOMENT THEY DIFFER, not on submit. */}
        <div className={'pwhint' + (tried && missing.pw ? ' pwbad' : '')} aria-live="polite">
          {pw2 && pw !== pw2 ? 'Those two passwords are not the same.'
            : (tried && missing.pw ? missing.pw : '')}
        </div>

        <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
          {error?.text || ''}
        </div>
      </main>

      <footer>
        {byWa ? (<>
          <button className="cta" type="submit">Send code on WhatsApp</button>
          <div className="deliv">Delivered free on WhatsApp · arrives in seconds</div>
          <div className="legend"><i className="req" aria-hidden="true">*</i> Required</div>
        </>) : (<>
          <button className="cta" type="submit">Send my code by email</button>
          <div className="legend"><i className="req" aria-hidden="true">*</i> Required</div>
          <div className="deliv">Arrives in seconds · check your spam folder if it doesn’t</div>
        </>)}
        {/* ⚠️ THE TERMS AND PRIVACY LINKS GO NOWHERE YET AND SAY SO BY NOT
            BEING LINKS. The locked screen has `href="#" onclick="return
            false"` — a link that does nothing. There are no Terms and no
            Privacy pages in this repository; writing them is not mine to do,
            and a link to a page that does not exist is the dead control this
            project keeps clearing out. Raised with Al. */}
        <div className="fine">By continuing you agree to Eleat’s Terms &amp; Privacy.</div>
        <a className="forgot" href={withReturn(byWa ? '?signin=1&by=whatsapp' : '?signin=1', returnParam())}>Already on Eleat? Sign in</a>
      </footer>
    </form>
  );
}
